import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Loader2, Plus, Trash2, UserPlus } from "lucide-react";
import { errMsg, useApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { Card } from "./parts";

const ROLES = { owner: "Titolare", manager: "Manager", editor: "Editor", viewer: "Lettore" };
const QA = /test|qa|demo/i;

function Grant({ u, tenants, onDone }) {
  const api = useApi();
  const [t, setT] = useState("");
  const [r, setR] = useState("viewer");
  const add = async () => { try { await api.post(`/dev/test-users/${u.id}/membership`, { tenant_id: t, role: r }); toast.success("Accesso aggiornato"); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={t} onValueChange={setT}><SelectTrigger className="h-8 w-56 text-xs" data-testid={`test-user-tenant-${u.id}`}><SelectValue placeholder="Spazio di prova…" /></SelectTrigger>
        <SelectContent>{tenants.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent></Select>
      <Select value={r} onValueChange={setR}><SelectTrigger className="h-8 w-32 text-xs" data-testid={`test-user-role-${u.id}`}><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(ROLES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
      <Button size="sm" variant="outline" className="h-8" disabled={!t} onClick={add} data-testid={`test-user-grant-${u.id}`}><Plus size={13} className="mr-1" />Dai accesso</Button>
    </div>);
}

function UserRow({ u, tenants, onChanged, onDelete }) {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const link = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/dev/test-users/${u.id}/login-link`);
      await navigator.clipboard.writeText(window.location.origin + data.path);
      toast.success("Link di accesso copiato (valido 10 minuti, una sola volta): incollalo in una finestra anonima");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <li className="space-y-2 rounded-lg border p-3" data-testid={`test-user-${u.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-0 flex-1"><p className="text-sm font-medium text-slate-900">{u.label}</p><p className="truncate text-[11px] text-slate-500">{u.email}</p></div>
        <Button size="sm" className="h-8 bg-sky-600 hover:bg-sky-700" onClick={link} disabled={busy} data-testid={`test-user-link-${u.id}`}>{busy ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Copy size={13} className="mr-1" />}Copia link di accesso</Button>
        <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => onDelete(u)} aria-label="Elimina utente di test" data-testid={`test-user-delete-${u.id}`}><Trash2 size={14} /></Button>
      </div>
      <p className="text-xs text-slate-600" data-testid={`test-user-mems-${u.id}`}>{u.memberships.length ? u.memberships.map((m) => `${m.tenant_name}: ${ROLES[m.role] || m.role}`).join(" · ") : "Nessuno spazio"}</p>
      <Grant u={u} tenants={tenants} onDone={onChanged} />
    </li>);
}

// DEV only (hidden elsewhere): synthetic verified test accounts for role QA, no email codes.
export default function TestUsers() {
  const api = useApi();
  const [on, setOn] = useState(false);
  const [list, setList] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [f, setF] = useState({ label: "", tenant_id: "", role: "viewer" });
  const [del, setDel] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/dev/test-users").then(({ data }) => setList(data)).catch(() => {}), [api]);
  useEffect(() => {
    api.get("/dev/test-users-enabled").then(({ data }) => { setOn(data.enabled); if (data.enabled) load(); }).catch(() => {});
    api.get("/tenants").then(({ data }) => setTenants((data.tenants || data).filter((t) => t.kind === "client" && QA.test(t.name)))).catch(() => {});
  }, [api, load]);
  if (!on) return null;
  const create = async () => {
    setBusy(true);
    try { await api.post("/dev/test-users", { label: f.label.trim(), role: f.role, tenant_id: f.tenant_id || null }); toast.success("Utente di test creato (già verificato)"); setF({ ...f, label: "" }); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Card title="Utenti di test (solo DEV)" testid="settings-test-users"
      desc="Account fittizi già verificati per provare ruoli e inviti, senza codici email. Email @example.com di prova (nessuna email reale). Solo spazi di prova (nome con TEST, QA o DEMO); i clienti reali e il Caseificio sono esclusi. Non esiste in produzione.">
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Input value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="Nome, es. Lettore Bottega" className="h-9 w-56" data-testid="test-user-new-label" />
        <Select value={f.tenant_id || "none"} onValueChange={(v) => setF({ ...f, tenant_id: v === "none" ? "" : v })}><SelectTrigger className="h-9 w-56 text-sm" data-testid="test-user-new-tenant"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="none">Nessuno spazio (per provare un invito)</SelectItem>{tenants.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}</SelectContent></Select>
        {f.tenant_id && <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}><SelectTrigger className="h-9 w-32 text-sm" data-testid="test-user-new-role"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(ROLES).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>}
        <Button onClick={create} disabled={!f.label.trim() || busy} data-testid="test-user-create">{busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <UserPlus size={14} className="mr-1.5" />}Crea utente di test</Button>
      </div>
      <ul className="mt-4 space-y-2" data-testid="test-user-list">{list.map((u) => <UserRow key={u.id} u={u} tenants={tenants} onChanged={load} onDelete={setDel} />)}</ul>
      <p className="mt-3 text-[11px] text-slate-500">«Copia link di accesso» genera un link monouso valido 10 minuti: aprilo in una finestra anonima/privata (o un altro browser) per entrare come quell'utente, mentre qui resti amministratore.</p>
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title={`Eliminare «${del?.label}»?`} destructive confirmLabel="Elimina utente di test"
        description="L'account di test viene cancellato da Clerk e perde l'accesso a tutti gli spazi." onConfirm={async () => { const u = del; setDel(null); try { await api.delete(`/dev/test-users/${u.id}`); toast.success("Utente di test eliminato"); load(); } catch (e) { toast.error(errMsg(e)); } }} />
    </Card>
  );
}
